import cookie from 'js-cookie';

export const WEB_PREFERENCE_COOKIE = 'ovo_web_preference';

export type WebPreference = 'legacy' | 'new-web';

type StoredWebPreference = {
  v: WebPreference;
  cid: string;
  at: number;
};

// Bounds a wrong inference in both directions: long enough to be a real escape
// hatch, short enough to re-offer new web once it has moved on.
const LEGACY_SUPPRESSION_MS = 3 * 24 * 60 * 60 * 1000;

const EXPIRES_DAYS = 365;

// Old and new web are separate origins — the cookie must sit on the shared parent domain.
const options =
  process.env.NODE_ENV === 'production'
    ? { domain: '.ovoenergy.com', expires: EXPIRES_DAYS }
    : { expires: EXPIRES_DAYS };

const readWebPreference = (): StoredWebPreference | null => {
  const raw = cookie.get(WEB_PREFERENCE_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.v !== 'legacy' && parsed?.v !== 'new-web') return null;
    if (typeof parsed.cid !== 'string' || typeof parsed.at !== 'number') {
      return null;
    }
    return parsed as StoredWebPreference;
  } catch {
    return null;
  }
};

export const setWebPreference = (
  preference: WebPreference,
  customerId: string,
): void => {
  cookie.set(
    WEB_PREFERENCE_COOKIE,
    JSON.stringify({ v: preference, cid: customerId, at: Date.now() }),
    options,
  );
};

export type RedirectDecision = 'allow' | 'suppress' | 'returned-from-new-web';

// cid match: the cookie is browser-scoped, so one person's choice must not silence another's redirect.
export const getRedirectDecision = (
  customerId: string | null | undefined,
  now: number = Date.now(),
): RedirectDecision => {
  const stored = readWebPreference();
  if (!stored || !customerId || stored.cid !== customerId) return 'allow';
  if (stored.v === 'new-web') return 'returned-from-new-web';
  return now - stored.at < LEGACY_SUPPRESSION_MS ? 'suppress' : 'allow';
};
